/**
 * PaymentSuccessPage - Result page after on-ramp checkout.
 *
 * Route: /payment/success
 * Despite the name, this page is the *single* result landing for ALL terminal
 * states (success / cancelled / failed) so providers only need one redirect URL.
 * The actual outcome is read from the `status` query param:
 *
 *   ?status=completed  → green checkmark, "Payment Successful"
 *   ?status=cancelled  → amber icon,      "Payment Cancelled"
 *   ?status=failed     → red icon,        "Payment Failed"
 *   (missing)          → falls back to 'completed' for legacy redirect links
 *
 * In every case the parent tab (BuyCrypto / PaymentPage) is notified via
 * BroadcastChannel + localStorage so it can close the awaiting-payment overlay
 * and route the user appropriately.
 *
 * Query params accepted (different providers use different names):
 *   orderId | order_id | partnerOrderId     - our internal order/purchase id
 *   status                                  - completed | cancelled | failed
 *   transactionHash | txHash | tx_hash      - blockchain tx hash (on completed)
 *   provider                                - e.g. guardarian, banxa, onmeta
 *   tid                                     - provider transaction id (Guardarian)
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import './PaymentPage.css';

const STATUS_CONFIG = {
  completed: {
    title: 'Payment Successful!',
    message: 'Your payment has been processed successfully.',
    accent: '#16a34a', // green
    icon: (
      <svg className="success-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="10" />
        <polyline points="9,12 11,14 15,10" />
      </svg>
    ),
  },
  cancelled: {
    title: 'Payment Cancelled',
    message: 'You cancelled the payment. No funds have been charged.',
    accent: '#d97706', // amber
    icon: (
      <svg className="success-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ color: '#d97706' }}>
        <circle cx="12" cy="12" r="10" />
        <line x1="9" y1="9" x2="15" y2="15" />
        <line x1="15" y1="9" x2="9" y2="15" />
      </svg>
    ),
  },
  failed: {
    title: 'Payment Failed',
    message: 'The provider could not process your payment. Please try again or contact support.',
    accent: '#dc2626', // red
    icon: (
      <svg className="success-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ color: '#dc2626' }}>
        <circle cx="12" cy="12" r="10" />
        <line x1="12" y1="8" x2="12" y2="13" />
        <line x1="12" y1="16" x2="12.01" y2="16" />
      </svg>
    ),
  },
};

const PaymentSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const [countdown, setCountdown] = useState(5);
  const [notified, setNotified] = useState(false);

  const orderId =
    searchParams.get('orderId') ||
    searchParams.get('order_id') ||
    searchParams.get('partnerOrderId');
  const rawStatus = (searchParams.get('status') || 'completed').toLowerCase();
  const status = STATUS_CONFIG[rawStatus] ? rawStatus : 'completed';
  const txHash =
    searchParams.get('transactionHash') ||
    searchParams.get('txHash') ||
    searchParams.get('tx_hash');
  const provider = searchParams.get('provider') || undefined;
  const tid = searchParams.get('tid') || undefined; // Guardarian provider tx id

  const ui = STATUS_CONFIG[status];

  // Notify parent tab about the terminal state — works for all 3 outcomes,
  // not just completed, so the parent can close its awaiting overlay either way.
  const notifyParentTab = useCallback(() => {
    if (notified) return;

    const paymentData = {
      type: 'PAYMENT_STATUS',
      orderId,
      status: status.toUpperCase(), // COMPLETED | CANCELLED | FAILED
      txHash,
      provider,
      tid,
      timestamp: Date.now(),
    };

    // Method 1: BroadcastChannel (modern browsers)
    try {
      const channel = new BroadcastChannel('kuberpay-payment');
      channel.postMessage(paymentData);
      channel.close();
      console.log('Notified parent via BroadcastChannel:', paymentData);
    } catch (err) {
      console.warn('BroadcastChannel not supported:', err);
    }

    // Method 2: localStorage (fallback for Safari and cross-tab communication)
    try {
      localStorage.setItem('kuberpay-payment-status', JSON.stringify(paymentData));
      console.log('Notified parent via localStorage:', paymentData);
    } catch (err) {
      console.warn('localStorage not available:', err);
    }

    setNotified(true);
  }, [orderId, status, txHash, provider, tid, notified]);

  useEffect(() => {
    notifyParentTab();

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          window.close();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [notifyParentTab]);

  const handleClose = () => {
    window.close();
    setTimeout(() => {
      alert('Please close this tab manually and return to the payment page.');
    }, 100);
  };

  return (
    <div className="payment-page">
      <div className="payment-container">
        <div className="payment-header">
          <div className="payment-logo">KuberCrypto</div>
        </div>

        <div className="payment-success">
          <div style={{ color: ui.accent }}>{ui.icon}</div>

          <h2 className="success-title" style={{ color: ui.accent }}>{ui.title}</h2>

          <p className="success-message">{ui.message}</p>

          {status === 'completed' && txHash && (
            <p className="success-txhash">
              Transaction: {txHash.substring(0, 10)}...{txHash.substring(txHash.length - 8)}
            </p>
          )}

          <div className="success-countdown">
            {countdown > 0 ? (
              <p>This window will close in <strong>{countdown}</strong> seconds...</p>
            ) : (
              <p>You can close this window now.</p>
            )}
          </div>

          <button className="close-button" onClick={handleClose}>
            Close Window
          </button>

          {status === 'completed' && (
            <p className="success-hint">
              You will be redirected back to the merchant automatically.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccessPage;
